import { useState, useEffect } from 'react';
import { Container } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import Page from 'src/components/Page';
import medSocialService from 'src/services/medSocialService';
import LoadingScreen from 'src/components/LoadingScreen';
import { useParams, useNavigate, Prompt } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Modes, BeforeUnload } from 'src/components';
import { setMode } from 'src/actions/formPermissionsActions';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import * as Sentry from '@sentry/browser';

import DeniedScreen from 'src/components/DeniedScreen';
import OfflineView from 'src/views/pages/OfflineView';
import { captureException } from '@sentry/browser';
import { serverState } from 'src/store/serverState';
import { useAppSelector } from 'src/store';
import { draiQuestionFormSchema, draiValidationSchema } from './validation';
import set from 'lodash-es/set';

const useStyles = makeStyles((theme) => ({
  root: { height: '100%' },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  contentContainer: {
    display: 'flex',
    position: 'relative',
    flex: '1 1 auto',
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto',
  },
  smallScreenContent: {
    [theme.breakpoints.down('md')]: {
      paddingTop: '180px !important',
    },
  },
}));

function DraiFormLayout({
  children,
  formId,
  pageName,
  permission: { readPermission, createPermission, updatePermission },
  initialValue,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { caseId, draiId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [data, setData] = useState({});
  const [onlySave, setOnlySave] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [errorStatus, setErrorStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { currentDRAIForm } = useAppSelector((state) => state.drai);
  const formMode = useAppSelector((state) => state.formPermissions.mode);
  const account = useAppSelector((state) => state.account);
  const banner = useAppSelector((state) => state.caseItem.banner);
  const [overrideWarning, setOverrideWarning] = useState(false);

  const checkPermission = (permission) =>
    account?.user?.permissions && account.user.permissions.includes(permission);

  const hasPermission = (permission) =>
    permission && checkPermission(permission);

  const denied = !hasPermission(readPermission);

  const loadSummary = async () => {
    dispatch(setMode('view'));
    try {
      setIsLoading(true);
      const draiData = await medSocialService.getDrai(caseId, draiId);
      draiData.interviewerFullName = `${draiData.interviewerFirstName} ${draiData.interviewerLastName}`;
      if (draiData?.isLoading) return;

      setData(draiData?.requestId ? draiData.data : draiData);
      setIsLoading(false);
      const isLocked =
        (draiData?.locked || banner?.locked || draiData?.oldVersion) && caseId;

      if (
        draiData?.submitted === false &&
        hasPermission(createPermission) &&
        !isLocked
      ) {
        dispatch(setMode('edit'));
        setIsHidden(false);
      } else if (hasPermission(updatePermission)) {
        dispatch(setMode(onlySave ? 'edit' : 'view'));
        setIsHidden(false);
      } else {
        setIsHidden(true);
      }

      if (isLocked) {
        setIsHidden(true);
      }
    } catch (err) {
      if (err?.response?.status) {
        setErrorStatus({
          status: err.response.status,
          linked: err.response.data,
          case: caseId,
        });
      } else {
        const errorId = captureException(err);
        setErrorStatus({
          status: 1001,
          case: caseId,
          linked: errorId,
        });
      }
    }
  };

  useEffect(() => {
    if (!caseId) {
      navigate('/404');
      return;
    }
    setOnlySave(false);
    dispatch(setMode('view'));
    if (draiId === 'new') {
      dispatch(setMode('edit'));
      setOnlySave(true);
    } else {
      loadSummary();
    }

    return () => {
      return null;
    };
  }, [draiId]);

  if (errorStatus?.status) {
    return (
      <OfflineView
        noHome
        errorInformation={errorStatus}
      />
    );
  }

  if (denied) {
    return <DeniedScreen />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Page
      className={classes.root}
      title={pageName}
    >
      <Formik
        initialValues={initialValue || data || {}}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validate={async (values) => {
          if (values.signatureBool) {
            try {
              const { type, version, questions } = currentDRAIForm;
              let schema = null;
              if (type && version) {
                schema = draiValidationSchema.concat(
                  draiQuestionFormSchema({ type, version, questions }),
                );
              }
              await schema.validate(values, {
                abortEarly: false,
                context: { answers: values.answers },
              });
            } catch (err) {
              const errors = { answers: {} };
              err.inner.forEach(({ path, message }) => {
                const segments = path.split('.');
                if (segments.length > 2 && segments[1] !== 'address') {
                  const question = segments[1];
                  errors.answers[question] = { answer: '' };
                  errors.answers[question].answer = message;
                } else {
                  set(errors, path, message);
                }
              });
              return errors;
            }
          }
        }}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setSubmitting(true);
            if (banner?.locked && formId !== 'approvalsCaseLock' && caseId) {
              return enqueueSnackbar('Case is locked!', {
                variant: 'error',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
              });
            }

            if (data?.id != null || data?.submitted) {
              await medSocialService.updateDrai(caseId, draiId, values);
              dispatch(setMode('view'));
            } else if (formId === 'medSocial' && draiId === 'new') {
              const newValues = await medSocialService.createDrai(
                caseId,
                values,
              );
              await dispatch(setMode('view'));
              setOverrideWarning(true);
              navigate(`/case/${caseId}/medSocial/${newValues.id}`);
            }

            enqueueSnackbar('Successfully saved!', {
              variant: 'success',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },
            });

            // We should remove this once everything is moved to RTK query
            // This helps ensure on updates that are not connected we refresh data
            dispatch(
              serverState.util.invalidateTags([
                'Case',
                'ReferralWorksheet',
                'Referrals',
              ]),
            );

            setStatus({ success: true });
            setSubmitting(false);
            setIsLoading(false);
          } catch (error) {
            if (error?.response?.data?.error === 'DUPLICATE_CASELOCK_STATUS') {
              enqueueSnackbar(error?.response?.data?.message, {
                variant: 'warning',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'center',
                },
              });
              setStatus({ success: false });
              setErrors({ submit: error?.message || error?.data?.message });
              setSubmitting(false);
              setIsLoading(false);
              return null;
            }
            enqueueSnackbar('Something went wrong!', {
              variant: 'error',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },
            });
            let sentryError;
            if (error?.response?.status) {
              sentryError = Sentry.captureException(
                new Error(
                  `${error.response.status}: ${error.response.statusText}`,
                ),

                {
                  tags: {
                    status: error.response.status,
                    case: caseId,
                    linked: error.response.data,
                  },
                },
              );
            } else {
              sentryError = Sentry.captureException(new Error(error), {
                tags: {
                  case: caseId,
                },
              });
            }
            Sentry.showReportDialog({
              subtitle:
                'Tell us what happened below without including any Protected Health Information.',
              subtitle2: '',
              eventId: sentryError,
              labelSubmit: 'Submit Report',
              user: account?.user?.firstName
                ? {
                    name: `${account.user.firstName} ${account.user.lastName}`,
                    email: account.user.email,
                  }
                : undefined,
            });
            setStatus({ success: false });
            setErrors({ submit: error?.message || error?.data?.message });
            setSubmitting(false);
            setIsLoading(false);
          }
        }}
      >
        {({ isSubmitting, resetForm, dirty, handleSubmit }) => (
          <>
            {!overrideWarning && dirty && formMode === 'edit' && (
              <>
                <Prompt
                  when={dirty && formMode === 'edit'}
                  message="You have unsaved changes. Are you sure you want to leave?"
                />

                <BeforeUnload
                  onBeforeUnload={() =>
                    'You have unsaved changes. Are you sure you want to leave?'
                  }
                />
              </>
            )}
            {isSubmitting && <LoadingScreen />}
            <Container maxWidth={false}>
              <Form
                noValidate
                autoComplete="off"
              >
                {!isHidden && !isSubmitting && (
                  <Modes
                    handleSubmit={handleSubmit}
                    onlySave={onlySave}
                    resetForm={resetForm}
                  />
                )}
                {children}
              </Form>
            </Container>
          </>
        )}
      </Formik>
    </Page>
  );
}

DraiFormLayout.propTypes = {
  children: PropTypes.any,
  formId: PropTypes.string,
  pageName: PropTypes.string,
  permission: PropTypes.object,
  initialValue: PropTypes.object,
};

DraiFormLayout.defaultProps = {
  permission: {
    readPermission: null,
    createPermission: null,
    updatePermission: null,
  },
};

export default DraiFormLayout;
